import {
  Component,
  ContentChildren,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { SaveButtonComponent } from '@app/shared/components/save-button/save-button.component';
import { TranscludeDirective } from '@app/shared/directives/transclude.directive';
import { UnsavedChangesService } from '@app/shared/services/unsaved-changes-service';
import { C5Helper } from '@app/utils/c5-helper';
import { ReservedValidationKeys } from '../validation-errors/validation-error-map';

@Component({
  selector: 'portal-modal-footer',
  templateUrl: './modal-footer.component.html',
  standalone: false,
})
export class ModalFooterComponent implements OnChanges, OnDestroy, OnInit {
  @Input()
  disabled: boolean;

  @Input()
  errors: ValidationErrors;

  hasCustomErrors: boolean;

  @Input()
  invalid: boolean;

  @Input()
  pending: boolean;

  @Input()
  pristine: boolean;

  @Input()
  saving: boolean;

  @Input() disabledOnPristine: boolean = true;
  @Input() disabledTooltip?: string;

  @Input() entry: any;

  @Input() closeOnly: boolean = false;

  /**
   * Shortcut for the state values (disabled, errors, invalid, pending, pristine)
   */
  @Input() form: UntypedFormGroup;

  @Output() onCancel: EventEmitter<void> = new EventEmitter();
  @Output() onReset: EventEmitter<void> = new EventEmitter();
  @Output() onSave: EventEmitter<void> = new EventEmitter();

  @ViewChild(SaveButtonComponent)
  saveButton!: SaveButtonComponent;

  @ContentChildren(TranscludeDirective)
  transclusions!: QueryList<TranscludeDirective>;

  private destroyRef = inject(DestroyRef);

  constructor(private unsavedChangesService: UnsavedChangesService) {}

  get buttonContent() {
    return C5Helper.isNew(this.entry) ? 'button.add' : 'button.save';
  }

  get isPristine() {
    return this.disabledOnPristine === true ? this.pristine : false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('pristine' in changes) {
      this.updateUnsavedChangesService(changes.pristine.currentValue);
    }

    if ('errors' in changes) {
      this.updateErrors(changes.errors.currentValue);
    }
  }

  ngOnDestroy() {
    this.unsavedChangesService.reset('modal');
  }

  ngOnInit() {
    if (this.form != null) {
      this.form.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.copyFromForm(this.form);
      });

      this.copyFromForm(this.form);
    }
  }

  emitCancel() {
    this.onCancel.emit();
  }

  emitReset() {
    this.onReset.emit();
  }

  emitSave() {
    this.onSave.emit();
  }

  focus(): void {
    this.saveButton.focus();
  }

  /**
   * Copy the state of the form to the other input parameters (this is used if the form input is set as a shortcut)
   * @param form
   */
  private copyFromForm(form: UntypedFormGroup) {
    this.disabled = form.disabled;
    this.errors = form.errors;
    this.updateErrors(form.errors);

    this.invalid = form.invalid;
    this.pending = form.pending;
    this.pristine = form.pristine;

    this.updateUnsavedChangesService();
  }

  private updateErrors(errors: ValidationErrors) {
    this.hasCustomErrors = false;

    if (errors != null) {
      const hasNonReservedProperties = Object.keys(errors).some(
        (key) => ReservedValidationKeys[key] === undefined
      );

      this.hasCustomErrors = hasNonReservedProperties || errors['custom'] !== undefined;
    }
  }

  /**
   * Check whether the current status is dirty and if so, ask the user to confirm.
   */
  private updateUnsavedChangesService(pristine: boolean = this.pristine): void {
    if (pristine === false) {
      this.unsavedChangesService.askForConfirmation('modal');
    } else {
      this.unsavedChangesService.reset('modal');
    }
  }
}
